import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import * as voicifyApi from '../../../api';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import ApplicationContainer from '../../../state/containers/ApplicationContainer';
import AppliedApplicationTemplateContainer from '../../../state/containers/AppliedApplicationTemplateContainer';
import ApplicationEnvironmentsContainer from '../../../state/containers/ApplicationEnvironmentsContainer';
import Button from '../../../components/general/Button';
import Loader from '../../../components/general/Loader';
import PageError from '../../../components/general/PageError';
import { color_defaults_white, color_shades_dark, color_highlights_blue, pastel_red } from '../../../constants/colors';
import ConfirmationDialog from '../../../components/structure/ConfirmationDialog';
import WarningConfirmation from '../../../components/general/WarningConfirmation';
import { SyncMenusRequest } from '../../../models/nlp/menu/SyncMenusRequest';
import { v4 as uuidv4 } from 'uuid';
import { aiAnsweringCommonAppName } from '../../../constants/sparkConstants/moduleConsts';
import TemplateFormFieldModel from '../../../models/templating/api/TemplateFormFieldModel';
import UpdateAppliedApplicationTemplateFormsRequest from '../../../models/templating/api/UpdateAppliedApplicationTemplateFormsRequest';
import * as XLSX from 'xlsx';
import MenuContainer from '../../../hooks/MenuContainer';
import MenuSyncContainer from '../../../hooks/MenuSyncContainer';
import useBulkAppOperations from '../../../hooks/useBulkAppOperations';

interface OrganizationManagementSettingsProps {
    organizationId: string;
    canManageQueues?: boolean;
    applicationContainer: ApplicationContainer;
    appliedTemplateContainer?: AppliedApplicationTemplateContainer;
    applicationEnvironmentsContainer?: ApplicationEnvironmentsContainer;
}

const OrganizationManagementSettings: React.FC<OrganizationManagementSettingsProps> = ({ organizationId, canManageQueues = false, applicationContainer, appliedTemplateContainer, applicationEnvironmentsContainer }) => {
    const [errors, setErrors] = useState<string[]>([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
    const [sharedTopicsApp, setSharedTopicsApp] = useState<ApplicationModel | null>(null);
    const [locationSpecificFields, setLocationSpecificFields] = useState<TemplateFormFieldModel[]>([]);
    const [isLoadingSharedTopics, setIsLoadingSharedTopics] = useState(false);
    const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState<string>('');
    const [importedData, setImportedData] = useState<any[] | null>(null);
    const [isApplying, setIsApplying] = useState(false);
    const [posConfigKey, setPosConfigKey] = useState<string>('');
    const [posConfigValue, setPosConfigValue] = useState<string>('');
    const [isUpdatingPosConfig, setIsUpdatingPosConfig] = useState(false);
    const [menuItemCounts, setMenuItemCounts] = useState<Map<string, { main: number; manage: number }>>(new Map());
    const [showManageMenuApps, setShowManageMenuApps] = useState(false);
    const [menuSyncApiKey, setMenuSyncApiKey] = useState<string>('');
    const [menuSyncConfig, setMenuSyncConfig] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [refreshCacheApiKey, setRefreshCacheApiKey] = useState<string>('');
    const [isMenuProcessing, setIsMenuProcessing] = useState(false);
    const [showClearCacheModal, setShowClearCacheModal] = useState(false);

    const menuContainer = MenuContainer.useContainer();
    const menuSyncContainer = MenuSyncContainer.useContainer();

    // Bulk app operations hook
    const { isProcessing: isModuleSyncing, logs: bulkOperationLogs, syncApps, publishApps, clearLogs } = useBulkAppOperations({
        organizationId,
        appliedTemplateContainer,
        applicationEnvironmentsContainer
    });

    // Combined processing state
    const isPublishing = isModuleSyncing || isMenuProcessing;

    useEffect(() => {
        // Apps are already loaded by parent component (OrganizationSettingsTabs)
        // No need to call getBaseApplicationsForOrg here
        if (appliedTemplateContainer) {
            loadSharedTopicsFields();
        }
    }, [organizationId]);
    // Filter out manage menu apps themselves - use same logic as ApplicationDashboard
    // Keep location apps that have associated manage menu apps
    const appsWithMenuManagement = applicationContainer.state.baseOrgApplications.filter(app => {
        // If showing manage menu apps, include everything
        if (showManageMenuApps) {
            return true;
        }

        // Filter out apps that ARE manage menu apps based on their name
        if (app?.name?.toLowerCase()?.includes("manage menu") ||
            app?.name?.toLowerCase()?.includes("manage entities") ||
            (app?.name?.toLowerCase()?.startsWith("x -") && app?.name?.toLowerCase()?.endsWith("menu"))) {
            return false;
        }
        // Filter out apps that ARE referenced as manage menu apps by other apps
        if (applicationContainer.state.baseOrgApplications.some(a => a.manageMenuApplicationId === app.id)) {
            return false;
        }
        return true;
    });

    // Filter apps based on search term with advanced syntax
    // Supports: !"exclude"|term1|term2 format
    // Pipe separates AND conditions, !" before quoted text means exclude
    const filteredApps = appsWithMenuManagement.filter(app => {
        if (!searchTerm.trim()) return true;

        const appName = app.name?.toLowerCase() || '';

        // Split by pipe to get individual search terms
        const terms = searchTerm.split('|').map(t => t.trim()).filter(t => t);

        // Separate exclusion terms and inclusion terms
        const exclusionTerms: string[] = [];
        const inclusionTerms: string[] = [];

        terms.forEach(term => {
            // Check if it's an exclusion term: !"text" or !'text'
            const exclusionMatch = term.match(/^!"(.+)"$/) || term.match(/^!'(.+)'$/);
            if (exclusionMatch) {
                exclusionTerms.push(exclusionMatch[1].toLowerCase());
            } else {
                // Remove quotes if present for regular terms
                const cleanTerm = term.replace(/^["']|["']$/g, '');
                inclusionTerms.push(cleanTerm.toLowerCase());
            }
        });

        // First check exclusions - if any exclusion term is found, exclude this app
        for (const excludeTerm of exclusionTerms) {
            if (appName.includes(excludeTerm)) {
                return false;
            }
        }

        // If no inclusion terms, show all (that aren't excluded)
        if (inclusionTerms.length === 0) {
            return true;
        }

        // Check if ALL inclusion terms match (AND logic)
        return inclusionTerms.every(term => appName.includes(term));
    });

    const loadMenuItemCounts = async () => {
        const counts = new Map<string, { main: number; manage: number }>();

        for (const app of appsWithMenuManagement) {
            if (!app.manageMenuApplicationId) {
                console.warn(`App ${app.name} has no manageMenuApplicationId`);
                continue;
            }
            try {
                // Get menu items for main app
                const mainResult = await voicifyApi.getVoicifyMenuItems(app.id);
                const mainCount = mainResult?.resultType === "Ok" && mainResult.data ? mainResult.data.length : 0;

                // Get menu items for manage menu app
                const manageResult = await voicifyApi.getVoicifyMenuItems(app.manageMenuApplicationId);
                const manageCount = manageResult?.resultType === "Ok" && manageResult.data ? manageResult.data.length : 0;

                counts.set(app.id, { main: mainCount, manage: manageCount });
            } catch (error) {
                console.error(`Error loading menu items for ${app.name}:`, error);
            }
        }

        setMenuItemCounts(counts);
    };

    const loadSharedTopicsFields = async () => {
        setIsLoadingSharedTopics(true);
        try {
            // Get all org applications - use loadAll: true to ensure we get all apps
            const appsResult = await applicationContainer.searchForOrgApplications(organizationId, 1, 50, undefined, undefined, true);
            const apps = appsResult?.data?.applications ?? [];

            // Find the Shared Topics app - try exact match first, then partial match
            let sharedTopics = apps.find(app =>
                app.name?.toLowerCase()?.trim() === aiAnsweringCommonAppName.toLowerCase() && app.allowsTemplating
            );

            // If not found, try partial match
            if (!sharedTopics) {
                sharedTopics = apps.find(app =>
                    (app.name?.toLowerCase()?.includes('shared topics') || app.name?.toLowerCase()?.includes('shared configuration'))
                    && app.allowsTemplating
                );
            }

            // If still not found, try without allowsTemplating requirement
            if (!sharedTopics) {
                sharedTopics = apps.find(app =>
                    app.name?.toLowerCase()?.includes('shared topics')
                    ||
                    app.name?.toLowerCase()?.includes('shared configuration')
                );
            }

            console.log('Found shared topics app:', sharedTopics);

            if (sharedTopics && appliedTemplateContainer) {
                setSharedTopicsApp(sharedTopics);

                // Load applied templates for the shared topics app
                const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(sharedTopics.id);

                if (appliedTemplatesResult?.resultType === "Ok") {
                    const appliedTemplates = appliedTemplatesResult.data;

                    // Extract location-specific fields
                    const locationFields: TemplateFormFieldModel[] = [];
                    const fieldSet = new Set<string>();

                    appliedTemplates.forEach(appliedTemplate => {
                        appliedTemplate.templateForm?.templateFormSections?.forEach(section => {
                            section.templateFormFields?.forEach(field => {
                                // A field is location-specific if it has a value matching its associated variable
                                const fieldValue = appliedTemplate.templateFormFieldValues?.find(
                                    v => v.templateFormFieldId === field.id
                                );

                                if (fieldValue?.associatedVariable && fieldValue.associatedVariable === fieldValue.value) {
                                    // Use title as unique identifier to avoid duplicates
                                    if (!fieldSet.has(field.title)) {
                                        fieldSet.add(field.title);
                                        locationFields.push({
                                            ...field,
                                            name: section.title // Store section title for display
                                        });
                                    }
                                }
                            });
                        });
                    });

                    setLocationSpecificFields(locationFields);
                }
            }
        } catch (error) {
            console.error('Error loading shared topics fields:', error);
        } finally {
            setIsLoadingSharedTopics(false);
        }
    };


    const toggleAppSelection = (appId: string) => {
        const newSelected = new Set(selectedApps);
        if (newSelected.has(appId)) {
            newSelected.delete(appId);
        } else {
            newSelected.add(appId);
        }
        setSelectedApps(newSelected);
    };

    const toggleSelectAll = () => {
        if (selectedApps.size === appsWithMenuManagement.length) {
            setSelectedApps(new Set());
        } else {
            setSelectedApps(new Set(appsWithMenuManagement.map(app => app.id)));
        }
    };

    const selectNonManageMenuOnly = () => {
        const nonManageMenuApps = appsWithMenuManagement.filter(app =>
            !(app.name ?? '').toLowerCase().includes('manage menu')
        );
        setSelectedApps(new Set(nonManageMenuApps.map(app => app.id)));
    };

    const selectLiveAppsOnly = () => {
        const liveApps = appsWithMenuManagement.filter(app => app.isLive === true);
        setSelectedApps(new Set(liveApps.map(app => app.id)));
    };

    const selectVisibleApps = () => {
        setSelectedApps(new Set(filteredApps.map(app => app.id)));
    };

    const toggleFieldSelection = (fieldTitle: string) => {
        const newSelected = new Set(selectedFields);
        if (newSelected.has(fieldTitle)) {
            newSelected.delete(fieldTitle);
        } else {
            newSelected.add(fieldTitle);
        }
        setSelectedFields(newSelected);
    };

    const toggleAllFields = () => {
        if (selectedFields.size === locationSpecificFields.length) {
            setSelectedFields(new Set());
        } else {
            setSelectedFields(new Set(locationSpecificFields.map(field => field.title)));
        }
    };

    const importFromExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setIsImporting(true);
            setErrors([]);
            setImportProgress('Reading Excel file...');

            // Read the file
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length < 2) {
                setErrors(['Excel file is empty or has no data rows']);
                return;
            }

            // Parse headers and data
            const headers = jsonData[0] as string[];
            const dataRows = jsonData.slice(1);

            // Validate required columns
            const locationIdIndex = headers.findIndex(h => h === 'Location ID');
            const locationNameIndex = headers.findIndex(h => h === 'Location Name');

            if (locationIdIndex === -1 || locationNameIndex === -1) {
                setErrors(['Excel file must have "Location ID" and "Location Name" columns']);
                return;
            }

            // Get field columns (everything after Location ID and Location Name)
            const fieldHeaders = headers.slice(2);

            setImportProgress(`Validating ${dataRows.length} locations...`);

            const validationErrors: string[] = [];
            const parsedData: any[] = [];

            // Validate and parse data without saving
            for (let i = 0; i < dataRows.length; i++) {
                const row = dataRows[i];
                const locationId = row[locationIdIndex];
                const locationName = row[locationNameIndex];

                if (!locationId) continue;

                setImportProgress(`Validating ${i + 1}/${dataRows.length}: ${locationName || locationId}...`);

                // Find the application by id (matching the export which uses app.id)
                let app = applicationContainer.state.baseOrgApplications.find(
                    a => a.id === locationId
                );

                if (!app) {
                    validationErrors.push(`Location not found: ${locationName || locationId}`);
                    continue;
                }

                // Parse field changes
                const fieldChanges: { fieldTitle: string; newValue: string }[] = [];
                for (let fieldIndex = 0; fieldIndex < fieldHeaders.length; fieldIndex++) {
                    const fieldTitle = fieldHeaders[fieldIndex];
                    const newValue = row[2 + fieldIndex]; // +2 to skip Location ID and Location Name

                    if (newValue !== undefined && newValue !== null && newValue !== '') {
                        fieldChanges.push({
                            fieldTitle,
                            newValue: String(newValue)
                        });
                    }
                }

                if (fieldChanges.length > 0) {
                    parsedData.push({
                        locationId,
                        locationName: locationName || app.name,
                        appId: app.id,
                        fieldChanges
                    });
                }
            }

            setImportProgress('');

            if (parsedData.length === 0) {
                setErrors(['No valid data found in Excel file']);
                return;
            }

            // Store imported data for preview
            setImportedData(parsedData);

            if (validationErrors.length > 0) {
                setErrors([
                    `Loaded ${parsedData.length} locations from Excel with ${validationErrors.length} warnings. Click "Apply Changes" to save the updates.`,
                    ...validationErrors.slice(0, 10),
                    ...(validationErrors.length > 10 ? [`...and ${validationErrors.length - 10} more warnings`] : [])
                ]);
            } else {
                setErrors([`Successfully loaded ${parsedData.length} locations from Excel. Review the changes below and click "Apply Changes" to save.`]);
            }

        } catch (error) {
            console.error('Error importing Excel:', error);
            setErrors(['Error importing Excel: ' + error.toString()]);
        } finally {
            setIsImporting(false);
            setImportProgress('');
            // Reset file input
            event.target.value = '';
        }
    };

    const applyChanges = async () => {
        if (!importedData || importedData.length === 0) {
            setErrors(['No imported data to apply']);
            return;
        }

        try {
            setIsApplying(true);
            setErrors([]);
            setImportProgress('Applying changes...');

            let successCount = 0;
            let errorCount = 0;
            const applyErrors: string[] = [];

            for (let i = 0; i < importedData.length; i++) {
                const locationData = importedData[i];
                console.log(locationData)

                setImportProgress(`Applying ${i + 1}/${importedData.length}: ${locationData.locationName}...`);

                try {
                    // Load applied templates for this app
                    const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(locationData.appId);

                    if (appliedTemplatesResult?.resultType !== "Ok") {
                        applyErrors.push(`Failed to load templates for ${locationData.locationName}`);
                        errorCount++;
                        continue;
                    }

                    const appliedTemplates = appliedTemplatesResult.data;

                    // Process each field change
                    for (const fieldChange of locationData.fieldChanges) {
                        // Find the applied template and field that matches this field title
                        let updated = false;
                        for (const appliedTemplate of appliedTemplates) {
                            for (const section of appliedTemplate.templateForm?.templateFormSections || []) {
                                const field = section.templateFormFields?.find(f => f.title === fieldChange.fieldTitle);

                                if (field) {
                                    console.log(field.title)
                                    // Find or create the field value
                                    let fieldValue = appliedTemplate.templateFormFieldValues?.find(
                                        v => v.templateFormFieldId === field.id
                                    );

                                    if (!fieldValue) {
                                        console.log("here1")
                                        fieldValue = {
                                            templateFormFieldId: field.id,
                                            value: fieldChange.newValue,
                                            associatedVariable: field.associatedVariable,
                                            label: field.label,
                                            fieldType: field.fieldType,
                                            placeholder: field.placeholder,
                                            defaultValue: field.defaultValue,
                                            tip: field.tip,
                                            title: field.title,
                                            appliedApplicationTemplateId: appliedTemplate.id
                                        };
                                        appliedTemplate.templateFormFieldValues.push(fieldValue);
                                    } else {
                                        console.log("here2")
                                        fieldValue.value = fieldChange.newValue;
                                    }

                                    updated = true;
                                    break;
                                }
                            }
                            if (updated) break;
                        }
                    }

                    // Save updated templates using updateAppliedTemplate for each template
                    let allSuccess = true;
                    console.log(appliedTemplates)
                    for (const appliedTemplate of appliedTemplates) {
                        const updateRequest = {
                            templateFormFieldValues: appliedTemplate.templateFormFieldValues,
                            dynamicFormSections: appliedTemplate.templateForm.templateFormSections.filter(s => s?.name)
                        };

                        const result = await appliedTemplateContainer.updateAppliedTemplate(
                            appliedTemplate.id,
                            updateRequest
                        );

                        if (result?.resultType !== "Ok") {
                            allSuccess = false;
                            break;
                        }
                    }

                    if (allSuccess) {
                        successCount++;
                    } else {
                        applyErrors.push(`Failed to save updates for ${locationData.locationName}`);
                        errorCount++;
                    }

                } catch (error) {
                    console.error(`Error applying changes for location ${locationData.locationName}:`, error);
                    applyErrors.push(`Error applying changes to ${locationData.locationName}: ${error.toString()}`);
                    errorCount++;
                }
            }

            setImportProgress('');

            if (applyErrors.length > 0) {
                setErrors([
                    `Applied changes with ${successCount} successes and ${errorCount} errors:`,
                    ...applyErrors.slice(0, 10),
                    ...(applyErrors.length > 10 ? [`...and ${applyErrors.length - 10} more errors`] : [])
                ]);
            } else {
                setErrors([`Successfully applied changes to ${successCount} locations!`]);
                setImportedData(null); // Clear imported data after successful apply
            }

        } catch (error) {
            console.error('Error applying changes:', error);
            setErrors(['Error applying changes: ' + error.toString()]);
        } finally {
            setIsApplying(false);
            setImportProgress('');
        }
    };

    const updateApplicationSecretsInPosConfig = async () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        try {
            setIsUpdatingPosConfig(true);
            setErrors([]);
            setImportProgress('Updating application secrets in POS config...');

            const selectedApplications = appsWithMenuManagement.filter(app => selectedApps.has(app.id));
            let successCount = 0;
            let errorCount = 0;
            const updateErrors: string[] = [];

            for (let i = 0; i < selectedApplications.length; i++) {
                const app = selectedApplications[i];
                setImportProgress(`Updating ${i + 1}/${selectedApplications.length}: ${app.name}...`);

                try {
                    if (!app.secret) {
                        updateErrors.push(`${app.name} has no application secret`);
                        errorCount++;
                        continue;
                    }

                    // Get current POS config
                    const currentConfigResult = await voicifyApi.getPOSConfig(app.id);
                    let configObj: any = {};

                    // Parse existing config if it exists
                    if (currentConfigResult?.resultType === "Ok" && currentConfigResult.data?.data) {
                        try {
                            configObj = JSON.parse(currentConfigResult.data.data);
                        } catch (parseError) {
                            console.warn(`Failed to parse existing config for ${app.name}, starting fresh`, parseError);
                        }
                    }

                    // Update the applicationSecret key with the app's secret
                    configObj["applicationSecret"] = app.secret;

                    // Stringify and update
                    const newConfigString = JSON.stringify(configObj, null, 2);
                    const updateResult = await voicifyApi.updatePOSConfig(app.id, newConfigString);

                    if (updateResult?.resultType === "Ok") {
                        // Call refresh cache if API key is provided
                        if (refreshCacheApiKey && app.posLocationId && app.posName) {
                            setImportProgress(`Refreshing cache ${i + 1}/${selectedApplications.length}: ${app.name}...`);
                            try {
                                await voicifyApi.refreshPOSCache(app.id, app.posLocationId, refreshCacheApiKey, app.posName);
                            } catch (cacheError) {
                                console.warn(`Failed to refresh cache for ${app.name}:`, cacheError);
                            }
                        }
                        successCount++;
                    } else {
                        updateErrors.push(`Failed to update ${app.name}: ${updateResult?.errors?.join(', ')}`);
                        errorCount++;
                    }
                } catch (error) {
                    console.error(`Error updating application secret for ${app.name}:`, error);
                    updateErrors.push(`Error updating ${app.name}: ${error.toString()}`);
                    errorCount++;
                }
            }

            setImportProgress('');

            if (updateErrors.length > 0) {
                setErrors([
                    `Updated application secrets with ${successCount} successes and ${errorCount} errors:`,
                    ...updateErrors.slice(0, 10),
                    ...(updateErrors.length > 10 ? [`...and ${updateErrors.length - 10} more errors`] : [])
                ]);
            } else {
                setErrors([`Successfully updated application secrets in POS config for ${successCount} locations!`]);
            }
        } catch (error) {
            console.error('Error updating application secrets:', error);
            setErrors(['Error updating application secrets: ' + error.toString()]);
        } finally {
            setIsUpdatingPosConfig(false);
            setImportProgress('');
        }
    };

    const generateMenuSyncConfig = () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        if (!menuSyncApiKey) {
            setErrors(['Please provide an API key']);
            return;
        }

        try {
            setErrors([]);
            const selectedApplications = appsWithMenuManagement.filter(app => selectedApps.has(app.id));

            if (selectedApplications.length === 0) {
                setErrors(['No applications selected']);
                return;
            }

            // First selected app is primary, rest are secondary
            const [primaryApp, ...secondaryApps] = selectedApplications;

            const config = {
                menuConfiguration: {
                    primaryLocation: {
                        manageMenuApplicationId: primaryApp.manageMenuApplicationId || '',
                        manageMenuApplicationName: applicationContainer.state.orgApplicationsList.find(a => a.id == primaryApp.manageMenuApplicationId)?.name,
                        liveApplicationId: primaryApp.id,
                        liveApplicationName: primaryApp.name,
                        apiKey: menuSyncApiKey
                    },
                    secondaryLocations: secondaryApps.map(app => ({
                        manageMenuApplicationId: app.manageMenuApplicationId || '',
                        manageMenuApplicationName: applicationContainer.state.orgApplicationsList.find(a => a.id == app.manageMenuApplicationId)?.name || '',
                        liveApplicationId: app.id,
                        liveApplicationName: app.name,
                        apiKey: menuSyncApiKey
                    }))
                }
            };

            const configJson = JSON.stringify(config, null, 2);
            setMenuSyncConfig(configJson);
            setErrors([`Generated menu sync configuration for ${selectedApplications.length} location${selectedApplications.length > 1 ? 's' : ''}`]);
        } catch (error) {
            console.error('Error generating menu sync config:', error);
            setErrors(['Error generating menu sync config: ' + error.toString()]);
        }
    };

    const copyMenuSyncConfig = () => {
        if (!menuSyncConfig) {
            setErrors(['No configuration to copy. Please generate the configuration first.']);
            return;
        }

        navigator.clipboard.writeText(menuSyncConfig)
            .then(() => {
                setErrors(['Configuration copied to clipboard!']);
            })
            .catch(err => {
                console.error('Failed to copy:', err);
                setErrors(['Failed to copy configuration to clipboard']);
            });
    };

    const updatePosConfigForSelectedLocations = async () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        if (!posConfigKey || !posConfigValue) {
            setErrors(['Please enter both a key and value for the POS config update']);
            return;
        }

        try {
            setIsUpdatingPosConfig(true);
            setErrors([]);
            setImportProgress('Updating POS config...');

            const selectedApplications = appsWithMenuManagement.filter(app => selectedApps.has(app.id));
            let successCount = 0;
            let errorCount = 0;
            const updateErrors: string[] = [];

            for (let i = 0; i < selectedApplications.length; i++) {
                const app = selectedApplications[i];
                setImportProgress(`Updating ${i + 1}/${selectedApplications.length}: ${app.name}...`);

                try {
                    // Get current POS config
                    const currentConfigResult = await voicifyApi.getPOSConfig(app.id);
                    let configObj: any = {};

                    // Parse existing config if it exists
                    if (currentConfigResult?.resultType === "Ok" && currentConfigResult.data?.data) {
                        try {
                            configObj = JSON.parse(currentConfigResult.data.data);
                        } catch (parseError) {
                            // If parsing fails, start with empty object
                            console.warn(`Failed to parse existing config for ${app.name}, starting fresh`, parseError);
                        }
                    }

                    // Update or add the key
                    // Try to parse the value as JSON first (for arrays, objects, etc.)
                    let parsedValue: any;
                    try {
                        parsedValue = JSON.parse(posConfigValue);
                    } catch {
                        // If it's not valid JSON, use it as a string
                        parsedValue = posConfigValue;
                    }
                    configObj[posConfigKey] = parsedValue;

                    // Stringify and update
                    const newConfigString = JSON.stringify(configObj, null, 2);
                    const updateResult = await voicifyApi.updatePOSConfig(app.id, newConfigString);

                    if (updateResult?.resultType === "Ok") {
                        // Call refresh cache if API key is provided
                        if (refreshCacheApiKey && app.posLocationId && app.posName) {
                            setImportProgress(`Refreshing cache ${i + 1}/${selectedApplications.length}: ${app.name}...`);
                            try {
                                const response = await voicifyApi.refreshPOSCache(app.id, app.posLocationId, refreshCacheApiKey, app.posName);
                                console.log(response)
                            } catch (cacheError) {
                                console.warn(`Failed to refresh cache for ${app.name}:`, cacheError);
                            }
                        }
                        successCount++;
                    } else {
                        updateErrors.push(`Failed to update ${app.name}: ${updateResult?.errors?.join(', ')}`);
                        errorCount++;
                    }
                } catch (error) {
                    console.error(`Error updating POS config for ${app.name}:`, error);
                    updateErrors.push(`Error updating ${app.name}: ${error.toString()}`);
                    errorCount++;
                }
            }

            setImportProgress('');

            if (updateErrors.length > 0) {
                setErrors([
                    `Updated POS config with ${successCount} successes and ${errorCount} errors:`,
                    ...updateErrors.slice(0, 10),
                    ...(updateErrors.length > 10 ? [`...and ${updateErrors.length - 10} more errors`] : [])
                ]);
            } else {
                setErrors([`Successfully updated POS config for ${successCount} locations! Key "${posConfigKey}" set to "${posConfigValue}"`]);
                // Clear inputs after successful update
                setPosConfigKey('');
                setPosConfigValue('');
            }
        } catch (error) {
            console.error('Error updating POS config:', error);
            setErrors(['Error updating POS config: ' + error.toString()]);
        } finally {
            setIsUpdatingPosConfig(false);
            setImportProgress('');
        }
    };

    const exportToExcel = async () => {
        if (selectedApps.size === 0 || selectedFields.size === 0) {
            setErrors(['Please select at least one application/location and one field to export']);
            return;
        }

        try {
            setIsLoadingSharedTopics(true);

            // Prepare data for Excel - use the selected apps from Menu Management section
            const selectedApplications = appsWithMenuManagement.filter(app => selectedApps.has(app.id));
            const selectedFieldsList = locationSpecificFields.filter(field => selectedFields.has(field.title));

            // Create header row
            const headers = ['Location ID', 'Location Name', ...selectedFieldsList.map(field => field.title)];

            // Create data rows with actual field values
            const data: any[][] = [];

            for (const app of selectedApplications) {
                const row: any[] = [
                    app.id,
                    app.name
                ];

                // Load applied templates for this location app
                if (appliedTemplateContainer) {
                    const appliedTemplatesResult = await appliedTemplateContainer.loadAppliedTemplates(app.id);

                    if (appliedTemplatesResult?.resultType === "Ok") {
                        const appliedTemplates = appliedTemplatesResult.data;

                        // For each selected field, find its value in this location's templates
                        for (const selectedField of selectedFieldsList) {
                            let fieldValue = '';

                            // Search through all applied templates for this location
                            for (const template of appliedTemplates) {
                                // Find the field by title
                                const field = template.templateForm?.templateFormSections
                                    ?.flatMap(section => section.templateFormFields)
                                    ?.find(f => f.title === selectedField.title);

                                if (field) {
                                    // Find the value for this field
                                    const value = template.templateFormFieldValues?.find(
                                        v => v.templateFormFieldId === field.id
                                    );

                                    if (value?.value) {
                                        fieldValue = value.value;
                                        break;
                                    }
                                }
                            }

                            row.push(fieldValue);
                        }
                    } else {
                        // If we can't load templates, add empty cells
                        selectedFieldsList.forEach(() => row.push(''));
                    }
                } else {
                    // If no template container, add empty cells
                    selectedFieldsList.forEach(() => row.push(''));
                }

                data.push(row);
            }

            // Create worksheet
            const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Location Specific Topics');

            // Generate file name with timestamp
            const timestamp = new Date().toISOString().split('T')[0];
            const fileName = `location_specific_topics_${timestamp}.xlsx`;

            // Save file
            XLSX.writeFile(wb, fileName);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            setErrors(['Error exporting to Excel: ' + error.toString()]);
        } finally {
            setIsLoadingSharedTopics(false);
        }
    };

    const publishAllMenus = async () => {
        setIsMenuProcessing(true);
        setErrors([]);
        try {
            const selectedApplications = appsWithMenuManagement.filter(app => selectedApps.has(app.id));

            if (selectedApplications.length === 0) {
                setErrors(['No applications selected']);
                return;
            }

            let publishedCount = 0;
            let skippedCount = 0;
            let errorCount = 0;

            for (const app of selectedApplications) {
                if (!app.manageMenuApplicationId) {
                    setErrors(prevErrors => [...prevErrors, `⚠ ${app.name}: No manage menu application configured (skipped)`]);
                    skippedCount++;
                    continue;
                }

                try {
                    await menuContainer.publishAllChanges(app, organizationId);
                    setErrors(prevErrors => [...prevErrors, `✓ ${app.name}: Successfully published`]);
                    publishedCount++;
                } catch (error) {
                    setErrors(prevErrors => [...prevErrors, `✗ ${app.name}: ${error.toString()}`]);
                    errorCount++;
                }
            }

            // Add summary
            setErrors(prevErrors => [
                `\nSummary: ${publishedCount} published, ${skippedCount} skipped, ${errorCount} errors`,
                ...prevErrors
            ]);
        } catch (error) {
            setErrors(prevErrors => [...prevErrors, `Fatal error: ${error.toString()}`]);
        } finally {
            setIsMenuProcessing(false);
            setShowConfirmationModal(false);
        }
    };

    const syncMenusWithPOS = async () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        setIsMenuProcessing(true);
        setErrors([]);

        try {
            const selectedApplications = appsWithMenuManagement.filter(app => selectedApps.has(app.id));
            const batchSize = 6;
            let syncedCount = 0;
            let errorCount = 0;
            let skippedCount = 0;

            setErrors([`Syncing menus with POS for ${selectedApplications.length} locations in batches of ${batchSize}...`]);

            // Process in batches of 6
            for (let i = 0; i < selectedApplications.length; i += batchSize) {
                const batch = selectedApplications.slice(i, i + batchSize);
                const batchNumber = Math.floor(i / batchSize) + 1;
                const totalBatches = Math.ceil(selectedApplications.length / batchSize);

                setErrors(prevErrors => [...prevErrors, `\nProcessing batch ${batchNumber}/${totalBatches} (${batch.length} locations)...`]);

                // Start all syncs in the batch
                const batchResults: { app: ApplicationModel; started: boolean }[] = [];

                for (const app of batch) {
                    const overallIndex = i + batch.indexOf(app) + 1;

                    if (!app.manageMenuApplicationId || !app.posLocationId || !app.posName) {
                        setErrors(prevErrors => [...prevErrors, `#${overallIndex}/${selectedApplications.length} ⚠ ${app.name}: Missing required config (skipped)`]);
                        skippedCount++;
                        batchResults.push({ app, started: false });
                        continue;
                    }

                    try {
                        setErrors(prevErrors => [...prevErrors, `#${overallIndex}/${selectedApplications.length} Starting menu sync for ${app.name}...`]);

                        await menuContainer.syncMenuWithPOS(app, {
                            retainAugmentation: true,
                            retainMenuBooleans: true,
                            addSynonymsOnly: false,
                            disableNewMenuItems: false,
                            orgId: organizationId
                        });

                        batchResults.push({ app, started: true });
                    } catch (error) {
                        setErrors(prevErrors => [...prevErrors, `#${overallIndex}/${selectedApplications.length} ✗ ${app.name}: ${error.toString()}`]);
                        errorCount++;
                        batchResults.push({ app, started: false });
                    }
                }

                // Wait for all syncs in the batch to complete
                const appsToWaitFor = batchResults.filter(r => r.started).map(r => r.app);

                if (appsToWaitFor.length > 0) {
                    setErrors(prevErrors => [...prevErrors, `Waiting for batch ${batchNumber}/${totalBatches} to complete (${appsToWaitFor.length} syncing)...`]);

                    let allComplete = false;
                    let waitAttempts = 0;
                    const maxWaitAttempts = 300; // 5 minutes max wait (300 * 1 second)

                    while (!allComplete && waitAttempts < maxWaitAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
                        waitAttempts++;

                        // Check if all apps in batch are done syncing
                        const stillSyncing = appsToWaitFor.filter(app =>
                            menuSyncContainer.isApplicationSyncing(app.id)
                        );

                        if (stillSyncing.length === 0) {
                            allComplete = true;
                            syncedCount += appsToWaitFor.length;
                            setErrors(prevErrors => [...prevErrors, `✓ Batch ${batchNumber}/${totalBatches} complete!`]);
                        } else if (waitAttempts % 10 === 0) {
                            // Log progress every 10 seconds
                            setErrors(prevErrors => [...prevErrors, `Still waiting... ${stillSyncing.length} locations syncing (${waitAttempts}s elapsed)`]);
                        }
                    }

                    if (!allComplete) {
                        setErrors(prevErrors => [...prevErrors, `⚠ Batch ${batchNumber}/${totalBatches} timed out after ${maxWaitAttempts} seconds`]);
                        errorCount += appsToWaitFor.length;
                    }
                }
            }

            setErrors(prevErrors => [...prevErrors,
            `\n✓ Menu sync with POS completed: ${syncedCount} successful, ${skippedCount} skipped, ${errorCount} errors`
            ]);
        } catch (error) {
            setErrors(prevErrors => [...prevErrors, `Fatal error: ${error.toString()}`]);
        } finally {
            setIsMenuProcessing(false);
        }
    };

    const clearCacheForSelectedLocations = async () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        setIsMenuProcessing(true);
        setErrors([]);

        let tempApiKeyId = '';
        let generatedApiKey = '';

        try {
            const selectedApplications = appsWithMenuManagement.filter(app => selectedApps.has(app.id));

            // Group selected applications by their posName (case-insensitive)
            const appsByPos: Record<string, typeof selectedApplications> = {};
            const skippedApps: string[] = [];

            selectedApplications.forEach(app => {
                if (!app.posName || !app.posLocationId) {
                    skippedApps.push(app.name);
                    return;
                }
                const key = app.posName.trim().toLowerCase();
                if (!appsByPos[key]) {
                    appsByPos[key] = [];
                }
                appsByPos[key].push(app);
            });

            if (skippedApps.length > 0) {
                setErrors(prev => [
                    ...prev,
                    `Skipped ${skippedApps.length} locations due to missing POS config: ${skippedApps.join(', ')}`
                ]);
            }

            const posNamesToRefresh = Object.keys(appsByPos);
            if (posNamesToRefresh.length === 0) {
                setErrors(prev => [...prev, 'No valid locations with POS config were selected.']);
                setIsMenuProcessing(false);
                setShowClearCacheModal(false);
                return;
            }

            // Generate temp API Key
            setErrors(prev => [...prev, 'Generating temporary admin API key for cache clearing...']);
            const keyResponse = await voicifyApi.createOrganizationAdminApiKey(organizationId, {
                name: `Temp Cache Clear Key - ${Date.now()}`
            });

            if (keyResponse?.resultType !== 'Ok' || !keyResponse?.data?.key || !keyResponse?.data?.id) {
                const errorMsg = keyResponse?.errors?.join(', ') || 'Unknown error';
                setErrors(prev => [...prev, `✗ Failed to generate temporary API key: ${errorMsg}`]);
                setIsMenuProcessing(false);
                setShowClearCacheModal(false);
                return;
            }

            tempApiKeyId = keyResponse.data.id;
            generatedApiKey = keyResponse.data.key;
            setErrors(prev => [...prev, '✓ Temporary API key generated successfully.']);

            // Clear POS Cache for each posName group
            let successCount = 0;
            let errorCount = 0;

            for (const posName of posNamesToRefresh) {
                const appsInGroup = appsByPos[posName];
                const storeIds = appsInGroup.map(app => app.posLocationId).filter(Boolean) as string[];
                const anyAppId = appsInGroup[0].id;

                setErrors(prev => [...prev, `Clearing cache for POS '${posName}' (Stores: ${storeIds.join(', ')})...`]);

                try {
                    const response = await voicifyApi.clearPOSCache(posName, anyAppId, storeIds, generatedApiKey);
                    if (response?.resultType === 'Ok') {
                        setErrors(prev => [...prev, `✓ Successfully cleared cache for POS '${posName}'`]);
                        successCount++;
                    } else {
                        const errorMsg = response?.errors?.join(', ') || 'Unknown error';
                        setErrors(prev => [...prev, `✗ Failed to clear cache for POS '${posName}': ${errorMsg}`]);
                        errorCount++;
                    }
                } catch (error) {
                    setErrors(prev => [...prev, `✗ Error clearing cache for POS '${posName}': ${error.toString()}`]);
                    errorCount++;
                }
            }

            setErrors(prev => [
                ...prev,
                `\nCache clear completed: ${successCount} successful, ${errorCount} failed.`
            ]);

        } catch (error) {
            console.error('Error during clear cache operation:', error);
            setErrors(prev => [...prev, `An error occurred: ${error.toString()}`]);
        } finally {
            if (tempApiKeyId) {
                setErrors(prev => [...prev, 'Cleaning up temporary admin API key...']);
                try {
                    await voicifyApi.deleteApiKey(tempApiKeyId);
                    setErrors(prev => [...prev, '✓ Temporary API key cleaned up successfully.']);
                } catch (cleanupError) {
                    console.error('Failed to delete temp API key:', cleanupError);
                    setErrors(prev => [...prev, `⚠ Failed to delete temporary API key: ${cleanupError.toString()}`]);
                }
            }
            setIsMenuProcessing(false);
            setShowClearCacheModal(false);
        }
    };

    const publishTemplatesForSelectedLocations = async () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        const selectedApplications = appsWithMenuManagement.filter(app => selectedApps.has(app.id));
        await publishApps(selectedApplications);
    };

    const moduleSyncAllLocations = async () => {
        // Sync all location apps (except shared topics and manage menu apps)
        const locationApps = applicationContainer.state.baseOrgApplications.filter(app =>
            !app.name.toLowerCase().includes('manage menu') &&
            app.id !== sharedTopicsApp?.id
        );

        await syncApps(locationApps, sharedTopicsApp);
        setShowConfirmationModal(false);
    };

    const moduleSyncSelectedLocations = async () => {
        if (selectedApps.size === 0) {
            setErrors(['Please select at least one location']);
            return;
        }

        const selectedApplications = appsWithMenuManagement.filter(app => selectedApps.has(app.id));
        await syncApps(selectedApplications, sharedTopicsApp);
    };

    // Parent component (OrganizationSettingsTabs) handles loading state
    // No need to show loader here


    return (
        <Container>
            <Header>
                <Title>Menu Management</Title>
                <Description>
                    Train and publish menus for all applications in this organization
                </Description>
            </Header>

            {appsWithMenuManagement.length === 0 ? (
                <EmptyState>
                    <EmptyStateText>No applications with menu management found in this organization.</EmptyStateText>
                </EmptyState>
            ) : (
                <>
                    <SearchBar>
                        <SearchInput
                            type="text"
                            placeholder='Search by name (e.g., location1|location2|!"exclude")'
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <SearchInfo>
                            {searchTerm ? (
                                `Showing ${filteredApps.length} of ${appsWithMenuManagement.length} applications`
                            ) : (
                                'Use | to separate search terms (AND). Use !"text" to exclude apps containing text. Example: !"[FD]"|!"[TJ]"'
                            )}
                        </SearchInfo>
                    </SearchBar>

                    <ActionsBar>
                        <SelectionControls>
                            <SelectionInfo>
                                <Checkbox
                                    type="checkbox"
                                    checked={selectedApps.size === appsWithMenuManagement.length}
                                    onChange={toggleSelectAll}
                                />
                                <span>{selectedApps.size} of {appsWithMenuManagement.length} selected</span>
                            </SelectionInfo>
                            <Button
                                themes={['secondary-small']}
                                text="Select Non-Manage Menu Only"
                                onClick={selectNonManageMenuOnly}
                            />
                            <Button
                                themes={['secondary-small']}
                                text={`Select Visible (${filteredApps.length})`}
                                onClick={selectVisibleApps}
                                disabled={filteredApps.length === 0}
                            />
                            <Button
                                themes={['secondary-small']}
                                text="Select Live Apps Only"
                                onClick={selectLiveAppsOnly}
                            />
                            <Button
                                themes={['secondary-small']}
                                text="Load Menu Counts"
                                onClick={loadMenuItemCounts}
                            />
                            <Button
                                themes={['secondary-small']}
                                text={showManageMenuApps ? "Hide Manage Menu Apps" : "Show Manage Menu Apps"}
                                onClick={() => setShowManageMenuApps(!showManageMenuApps)}
                            />
                        </SelectionControls>
                        <ButtonGroup>
                            {canManageQueues && <>
                                <Button
                                    themes={['secondary']}
                                    text="Module Sync All Locations"
                                    onClick={moduleSyncAllLocations}
                                    disabled={isPublishing || !sharedTopicsApp}
                                />
                                <Button
                                    themes={['secondary']}
                                    text={`Module Sync Selected (${selectedApps.size})`}
                                    onClick={moduleSyncSelectedLocations}
                                    disabled={selectedApps.size === 0 || isPublishing || !sharedTopicsApp}
                                />
                                <Button
                                    themes={['secondary']}
                                    text={`Backup and Publish (${selectedApps.size})`}
                                    onClick={publishTemplatesForSelectedLocations}
                                    disabled={selectedApps.size === 0 || isPublishing || !applicationEnvironmentsContainer}
                                />
                            </>}
                            <Button
                                themes={['secondary']}
                                text={`Sync with POS (${selectedApps.size})`}
                                onClick={syncMenusWithPOS}
                                disabled={selectedApps.size === 0 || isPublishing}
                            />
                            <Button
                                themes={['secondary']}
                                text={`Clear Cache (${selectedApps.size})`}
                                onClick={() => setShowClearCacheModal(true)}
                                disabled={selectedApps.size === 0 || isPublishing}
                            />
                            <Button
                                themes={['primary']}
                                text={`Publish ${selectedApps.size} ${selectedApps.size === 1 ? 'Menu' : 'Menus'}`}
                                onClick={() => setShowConfirmationModal(true)}
                                disabled={selectedApps.size === 0 || isPublishing}
                            />
                        </ButtonGroup>
                    </ActionsBar>

                    {bulkOperationLogs.length > 0 && (
                        <SyncLogSection>
                            <SyncLogHeader>
                                <span>Sync Progress</span>
                                {!isPublishing && (
                                    <Button
                                        themes={['secondary-small']}
                                        text="Clear Log"
                                        onClick={clearLogs}
                                    />
                                )}
                            </SyncLogHeader>
                            <SyncLogArea>
                                {bulkOperationLogs.map((log, index) => (
                                    <SyncLogLine key={index}>{log}</SyncLogLine>
                                ))}
                            </SyncLogArea>
                        </SyncLogSection>
                    )}

                    <ApplicationsList>
                        {filteredApps.map(app => (
                            <ApplicationCard key={app.id} selected={selectedApps.has(app.id)}>
                                <CardContent onClick={() => toggleAppSelection(app.id)}>
                                    <Checkbox
                                        type="checkbox"
                                        checked={selectedApps.has(app.id)}
                                        onChange={() => toggleAppSelection(app.id)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <ApplicationInfo>
                                        <ApplicationName>{app.name}</ApplicationName>
                                        {app.posName && (
                                            <ApplicationMeta>POS: {app.posName}</ApplicationMeta>
                                        )}
                                        {menuItemCounts.has(app.id) && (
                                            <>
                                                <ApplicationMeta>Main App Menu Items: {menuItemCounts.get(app.id)?.main ?? 0}</ApplicationMeta>
                                                <ApplicationMeta>Manage Menu Items: {menuItemCounts.get(app.id)?.manage ?? 0}</ApplicationMeta>
                                            </>
                                        )}
                                    </ApplicationInfo>
                                </CardContent>
                            </ApplicationCard>
                        ))}
                    </ApplicationsList>
                </>
            )}

            {showConfirmationModal && (
                <ConfirmationDialog
                    title="Publish Menus"
                    deleteText="Yes, Publish"
                    warning={true}
                    disabled={isPublishing}
                    onClose={() => setShowConfirmationModal(false)}
                    onConfirm={publishAllMenus}
                >
                    {isPublishing ? (
                        <Loader />
                    ) : (
                        <WarningConfirmation
                            useWarningColor={true}
                            text={`Are you sure you want to train and publish menus for ${selectedApps.size} ${selectedApps.size === 1 ? 'application' : 'applications'}? This will update the live assistants with the latest menu changes.`}
                        />
                    )}
                </ConfirmationDialog>
            )}

            {showClearCacheModal && (() => {
                const uniquePosNames = Array.from(new Set(
                    appsWithMenuManagement
                        .filter(app => selectedApps.has(app.id))
                        .map(app => app.posName?.trim())
                        .filter(Boolean)
                ));
                const formattedPosNames = uniquePosNames.map(name => name.charAt(0).toUpperCase() + name.slice(1));
                const modalTitle = formattedPosNames.length === 1
                    ? `Clear ${formattedPosNames[0]} Cache`
                    : formattedPosNames.length > 1
                        ? `Clear Cache (${formattedPosNames.join(', ')})`
                        : 'Clear POS Cache';

                const tightWarningConfirmationStyle = css`
                    margin: 24px !important;
                    img {
                        margin: 16px 0 !important;
                        width: 64px !important;
                        height: 64px !important;
                    }
                `;

                return (
                    <ConfirmationDialog
                        title={modalTitle}
                        deleteText={isMenuProcessing ? "Clearing..." : "Yes, Clear Cache"}
                        warning={true}
                        disabled={isMenuProcessing}
                        onClose={() => setShowClearCacheModal(false)}
                        onConfirm={clearCacheForSelectedLocations}
                    >
                        {isMenuProcessing ? (
                            <Loader />
                        ) : (
                            <WarningConfirmation
                                useWarningColor={true}
                                className={tightWarningConfirmationStyle}
                                text={`Are you sure you want to clear the POS cache for the ${selectedApps.size} selected ${selectedApps.size === 1 ? 'location' : 'locations'}? This will automatically generate a secure temporary API key, trigger the cache clear, and clean up afterwards.`}
                            />
                        )}
                    </ConfirmationDialog>
                );
            })()}

            <Header style={{ marginTop: '48px' }}>
                <Title>POS Config Update</Title>
                <Description>
                    Update or add a specific key-value pair in the POS config JSON for selected locations. Optionally provide an API key to refresh the cache after updating.
                </Description>
            </Header>

            <PosConfigSection>
                <InputGroup>
                    <Label>Refresh Cache API Key (Optional)</Label>
                    <Input
                        type="text"
                        placeholder="Enter x-api-key for cache refresh (optional)"
                        value={refreshCacheApiKey}
                        onChange={(e) => setRefreshCacheApiKey(e.target.value)}
                        disabled={isUpdatingPosConfig}
                    />
                </InputGroup>

                <QuickActionWrapper>
                    <Button
                        themes={['secondary']}
                        text={isUpdatingPosConfig ? 'Updating...' : `Update Application Secrets for ${selectedApps.size} ${selectedApps.size === 1 ? 'Location' : 'Locations'}`}
                        onClick={updateApplicationSecretsInPosConfig}
                        disabled={selectedApps.size === 0 || isUpdatingPosConfig}
                    />
                </QuickActionWrapper>

                <Divider>OR</Divider>

                <PosConfigInputs>
                    <InputGroup>
                        <Label>Config Key</Label>
                        <Input
                            type="text"
                            placeholder="e.g., storeNumber"
                            value={posConfigKey}
                            onChange={(e) => setPosConfigKey(e.target.value)}
                            disabled={isUpdatingPosConfig}
                        />
                    </InputGroup>
                    <InputGroup>
                        <Label>Config Value</Label>
                        <Input
                            type="text"
                            placeholder="e.g., 12345"
                            value={posConfigValue}
                            onChange={(e) => setPosConfigValue(e.target.value)}
                            disabled={isUpdatingPosConfig}
                        />
                    </InputGroup>
                </PosConfigInputs>
                <Button
                    themes={['primary']}
                    text={isUpdatingPosConfig ? 'Updating...' : `Update Custom Key for ${selectedApps.size} ${selectedApps.size === 1 ? 'Location' : 'Locations'}`}
                    onClick={updatePosConfigForSelectedLocations}
                    disabled={selectedApps.size === 0 || isUpdatingPosConfig || !posConfigKey || !posConfigValue}
                />
                {importProgress && isUpdatingPosConfig && <ProgressText>{importProgress}</ProgressText>}
            </PosConfigSection>

            <Header style={{ marginTop: '48px' }}>
                <Title>Generate Menu Sync Config</Title>
                <Description>
                    Generate a menu sync configuration JSON for selected locations. The first selected location will be the primary location, and the rest will be secondary locations. All locations will use the same API key.
                </Description>
            </Header>

            <MenuSyncSection>
                <InputGroup>
                    <Label>API Key</Label>
                    <Input
                        type="text"
                        placeholder="Enter API key for all locations"
                        value={menuSyncApiKey}
                        onChange={(e) => setMenuSyncApiKey(e.target.value)}
                    />
                </InputGroup>
                <ButtonGroup>
                    <Button
                        themes={['primary']}
                        text={`Generate Config for ${selectedApps.size} Location${selectedApps.size === 1 ? '' : 's'}`}
                        onClick={generateMenuSyncConfig}
                        disabled={selectedApps.size === 0 || !menuSyncApiKey}
                    />
                    {menuSyncConfig && (
                        <Button
                            themes={['secondary']}
                            text="Copy to Clipboard"
                            onClick={copyMenuSyncConfig}
                        />
                    )}
                </ButtonGroup>
                {menuSyncConfig && (
                    <ConfigDisplay>
                        <ConfigHeader>Generated Configuration:</ConfigHeader>
                        <ConfigCode>{menuSyncConfig}</ConfigCode>
                    </ConfigDisplay>
                )}
            </MenuSyncSection>

            {appliedTemplateContainer && (
                <>
                    <Header style={{ marginTop: '48px' }}>
                        <Title>Location-Specific Topics Export</Title>
                        <Description>
                            Select locations and topics to export as an Excel spreadsheet
                        </Description>
                    </Header>

                    {isLoadingSharedTopics ? (
                        <Loader />
                    ) : !sharedTopicsApp ? (
                        <EmptyState>
                            <EmptyStateText>No Shared Topics application found in this organization.</EmptyStateText>
                        </EmptyState>
                    ) : locationSpecificFields.length === 0 ? (
                        <EmptyState>
                            <EmptyStateText>No location-specific topics configured yet.</EmptyStateText>
                        </EmptyState>
                    ) : (
                        <>
                            <SelectionSection>
                                <SectionHeader>
                                    <SectionTitle>Select Topics</SectionTitle>
                                    <SelectionInfo>
                                        <Checkbox
                                            type="checkbox"
                                            checked={selectedFields.size === locationSpecificFields.length && locationSpecificFields.length > 0}
                                            onChange={toggleAllFields}
                                        />
                                        <span>{selectedFields.size} of {locationSpecificFields.length} selected</span>
                                    </SelectionInfo>
                                </SectionHeader>

                                <FieldsList>
                                    {locationSpecificFields.map((field, index) => (
                                        <FieldCard
                                            key={`${field.id}-${index}`}
                                            selected={selectedFields.has(field.title)}
                                            onClick={() => toggleFieldSelection(field.title)}
                                        >
                                            <Checkbox
                                                type="checkbox"
                                                checked={selectedFields.has(field.title)}
                                                onChange={() => toggleFieldSelection(field.title)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <FieldInfo>
                                                <FieldTitle>{field.title}</FieldTitle>
                                                {field.name && <FieldSection>Section: {field.name}</FieldSection>}
                                                {field.tip && <FieldDescription>{field.tip}</FieldDescription>}
                                            </FieldInfo>
                                        </FieldCard>
                                    ))}
                                </FieldsList>
                            </SelectionSection>

                            <Header style={{ marginTop: '32px' }}>
                                <Title>Import & Export</Title>
                                <Description>
                                    Import an Excel file to bulk update location-specific topics. The Excel file should have "Location ID" and "Location Name" columns, followed by the topic field columns. You can export data first to get the correct format.
                                </Description>
                            </Header>

                            <ImportExportButtonWrapper>
                                <ImportButtonWrapper>
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={importFromExcel}
                                        style={{ display: 'none' }}
                                        id="excel-import-input"
                                        disabled={isImporting || isApplying}
                                    />
                                    <label htmlFor="excel-import-input">
                                        <Button
                                            themes={['secondary']}
                                            text={isImporting ? 'Importing...' : 'Import from Excel'}
                                            onClick={() => document.getElementById('excel-import-input')?.click()}
                                            disabled={isImporting || isApplying}
                                        />
                                    </label>
                                    {importProgress && <ProgressText>{importProgress}</ProgressText>}
                                </ImportButtonWrapper>
                                <Button
                                    themes={['primary']}
                                    text={`Export to Excel (${selectedApps.size} locations, ${selectedFields.size} topics)`}
                                    onClick={exportToExcel}
                                    disabled={selectedApps.size === 0 || selectedFields.size === 0 || isImporting || isApplying}
                                />
                            </ImportExportButtonWrapper>

                            {importedData && importedData.length > 0 && (
                                <>
                                    <Header style={{ marginTop: '32px' }}>
                                        <Title>Review Imported Changes</Title>
                                        <Description>
                                            Review the imported data below. Click "Apply Changes" to save these updates to the database.
                                        </Description>
                                    </Header>

                                    <ImportPreviewSection>
                                        <ImportPreviewHeader>
                                            <span>{importedData.length} locations with pending changes</span>
                                            <Button
                                                themes={['primary']}
                                                text={isApplying ? 'Applying...' : `Apply Changes to ${importedData.length} Locations`}
                                                onClick={applyChanges}
                                                disabled={isApplying || isImporting}
                                            />
                                        </ImportPreviewHeader>

                                        <ImportPreviewList>
                                            {importedData.slice(0, 10).map((location, index) => (
                                                <ImportPreviewCard key={index}>
                                                    <LocationHeader>
                                                        <LocationName>{location.locationName}</LocationName>
                                                        <LocationId>ID: {location.locationId}</LocationId>
                                                    </LocationHeader>
                                                    <FieldChangesList>
                                                        {location.fieldChanges.map((change, changeIndex) => (
                                                            <FieldChangeItem key={changeIndex}>
                                                                <FieldChangeLabel>{change.fieldTitle}:</FieldChangeLabel>
                                                                <FieldChangeValue>{change.newValue}</FieldChangeValue>
                                                            </FieldChangeItem>
                                                        ))}
                                                    </FieldChangesList>
                                                </ImportPreviewCard>
                                            ))}
                                            {importedData.length > 10 && (
                                                <MoreLocationsNote>
                                                    ...and {importedData.length - 10} more locations
                                                </MoreLocationsNote>
                                            )}
                                        </ImportPreviewList>
                                    </ImportPreviewSection>
                                </>
                            )}
                        </>
                    )}
                </>
            )}

            <PageError errors={errors} />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 24px;
    font-weight: 600;
`;

const Description = styled.p`
    margin: 0;
    color: #666;
    font-size: 14px;
`;

const EmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const EmptyStateText = styled.p`
    color: #666;
    font-size: 14px;
    margin: 0;
`;

const ActionsBar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;

    @media (min-width: 1200px) {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
`;

const SelectionControls = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
`;

const ButtonGroup = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
`;

const SelectionInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
`;

const Checkbox = styled.input`
    width: 18px;
    height: 18px;
    cursor: pointer;
`;

const ApplicationsList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
`;

const ApplicationCard = styled.div<{ selected: boolean }>`
    border: 2px solid ${props => props.selected ? color_highlights_blue : color_shades_dark};
    border-radius: 8px;
    background: ${color_defaults_white};
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const CardContent = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
`;

const ApplicationInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
`;

const ApplicationName = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
`;

const ApplicationMeta = styled.p`
    margin: 0;
    font-size: 13px;
    color: #666;
`;

const SelectionSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 32px;
`;

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
`;

const FieldsList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
`;

const FieldCard = styled.div<{ selected?: boolean }>`
    padding: 16px;
    background: ${color_defaults_white};
    border: 2px solid ${props => props.selected ? color_highlights_blue : color_shades_dark};
    border-radius: 8px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: ${props => props.selected !== undefined ? 'pointer' : 'default'};
    transition: all 0.2s ease;

    ${props => props.selected !== undefined && `
        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
    `}
`;

const FieldInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
`;

const FieldTitle = styled.h4`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const FieldSection = styled.p`
    margin: 0;
    font-size: 12px;
    color: ${color_highlights_blue};
    font-weight: 500;
`;

const FieldDescription = styled.p`
    margin: 0;
    font-size: 12px;
    color: #666;
    line-height: 1.4;
`;

const ExportButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
`;

const ImportExportButtonWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 24px;
    gap: 16px;
`;

const ImportButtonWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ProgressText = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${color_highlights_blue};
    font-weight: 500;
`;

const ImportPreviewSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
`;

const ImportPreviewHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: ${color_defaults_white};
    border: 2px solid ${color_highlights_blue};
    border-radius: 8px;
    font-weight: 600;
`;

const ImportPreviewList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ImportPreviewCard = styled.div`
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const LocationHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${color_shades_dark};
`;

const LocationName = styled.h4`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
`;

const LocationId = styled.span`
    font-size: 13px;
    color: #666;
`;

const FieldChangesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FieldChangeItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const FieldChangeLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${color_highlights_blue};
`;

const FieldChangeValue = styled.span`
    font-size: 14px;
    color: #333;
    padding: 8px;
    background: #f5f5f5;
    border-radius: 4px;
`;

const MoreLocationsNote = styled.p`
    margin: 0;
    padding: 16px;
    text-align: center;
    font-size: 14px;
    color: #666;
    font-style: italic;
    background: ${color_defaults_white};
    border: 1px dashed ${color_shades_dark};
    border-radius: 8px;
`;

const PosConfigSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const PosConfigInputs = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
`;

const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Label = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const Input = styled.input`
    padding: 10px 12px;
    font-size: 14px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${color_defaults_white};
    color: #333;

    &:focus {
        outline: none;
        border-color: ${color_highlights_blue};
    }

    &:disabled {
        background: #f5f5f5;
        cursor: not-allowed;
    }

    &::placeholder {
        color: #999;
    }
`;

const SearchBar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const SearchInput = styled.input`
    padding: 12px 16px;
    font-size: 14px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${color_defaults_white};
    color: #333;

    &:focus {
        outline: none;
        border-color: ${color_highlights_blue};
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
    }

    &::placeholder {
        color: #999;
    }
`;

const SearchInfo = styled.div`
    font-size: 13px;
    color: #666;
    font-style: italic;
`;


const QuickActionWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    text-align: center;
    color: #999;
    font-size: 14px;
    font-weight: 600;
    margin: 8px 0;

    &::before,
    &::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid ${color_shades_dark};
    }

    &::before {
        margin-right: 16px;
    }

    &::after {
        margin-left: 16px;
    }
`;

const MenuSyncSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const ConfigDisplay = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
`;

const ConfigHeader = styled.h4`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const ConfigCode = styled.pre`
    margin: 0;
    padding: 16px;
    background: #f5f5f5;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    font-size: 13px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
    overflow-x: auto;
    color: #333;
    line-height: 1.5;
`;

const SyncLogSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 16px 0;
`;

const SyncLogHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const SyncLogArea = styled.div`
    max-height: 300px;
    overflow-y: auto;
    padding: 16px;
    background: #1e1e1e;
    border-radius: 8px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
    font-size: 12px;
`;

const SyncLogLine = styled.div`
    color: #d4d4d4;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;

    &:nth-of-type(odd) {
        color: #9cdcfe;
    }
`;

export default OrganizationManagementSettings;
